import React from "react";
import {
  createFileRoute,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";
import {
  Box,
  BreadcrumbItem,
  Button,
  Icon,
  Separator,
  Spinner,
  Tabs,
  Text,
} from "@salestrak/ui";

import {
  SortableTab,
  SortableTabsList,
  TabsPillIcon,
  type TabsPillIconProps,
} from "@salestrak/ui";
import { PageHeaderBreadcrumb } from "#app/components/page-header";
import { EntityBreadcrumbLink } from "#app/components/entity";

import { AccessDenied } from "#app/components/permissions/require-permission";
import { WorkflowShareDialog } from "#app/components/workflows/workflow-share-dialog";
import { WorkflowAutomationsPopover } from "#app/components/automations/workflow-automations-popover";
import { useAutomationsEnabled } from "#app/lib/hooks/use-automations-enabled";
import { useAppData, type WorkspaceWorkflow as Workflow } from "#app/lib/data";
import { canManageSharing } from "#app/lib/utils/access-level";
import { issueFilterConfig } from "#app/lib/filters/configs/issue-filter-config";
import { FilterPanel } from "#app/components/filters/filter-panel";
import {
  IssueFilterProvider,
  useIssueFilters,
} from "#app/lib/filters/issue-filter-context";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { useWorkflowId } from "#app/lib/hooks/use-workflow-id";
import { useWidgetsFlag } from "#app/lib/hooks/use-widgets-flag";
import {
  useWorkflowViewTabOrder,
  type WorkflowViewTab,
} from "#app/lib/hooks/use-view-tab-order";
import { useAnalytics, AnalyticsContext, IssueEvent, AnalyticsScope } from "#app/lib/analytics";

/**
 * Single-workflow chrome wrapping the view routes (`/board`, `/list`, and the
 * flag-gated `/dashboard`): the breadcrumb masthead, Share action, view tabs,
 * object-level access guard, and shared filter context + filter-panel overlay.
 * The header renders here once, above `<Outlet />`, so it's identical across all
 * views; each route renders only its own body and toolbar below the
 * separator.
 */

// Chip colors match the team view tabs' Board/List/Dashboard entries — one
// visual language for the same view concept on both surfaces.
const TAB_META: Record<
  WorkflowViewTab,
  { icon: TabsPillIconProps["icon"]; color: TabsPillIconProps["color"]; label: string }
> = {
  list: { icon: "list", color: "violet", label: "List" },
  board: { icon: "columns-3", color: "light-blue", label: "Board" },
  dashboard: { icon: "chart-column-increasing", color: "orange", label: "Dashboard" },
};

function useActiveTab(): WorkflowViewTab {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // pathname ends with "/list", "/board", or "/dashboard" — grab the last segment
  const lastSegment = pathname.split("/").pop();
  if (lastSegment === "list") return "list";
  if (lastSegment === "dashboard") return "dashboard";
  return "board";
}

function WorkflowLayout() {
  const workflowId = useWorkflowId();
  return (
    <IssueFilterProvider key={workflowId}>
      <WorkflowLayoutInner />
    </IssueFilterProvider>
  );
}

export const Route = createFileRoute(
  "/(app)/$workspace/workflow/$workflowId",
)({ component: WorkflowLayout });

function WorkflowLayoutInner() {
  const workflowId = useWorkflowId();
  const { replace } = useAppNavigate();
  const { track } = useAnalytics();
  const { workflows, currentUser, isLoading } = useAppData();
  const { filters, filterPanelOpen, closeFilterPanel } = useIssueFilters();

  const workflow = React.useMemo(
    () => workflows.find((wf: Workflow) => wf.workflowId === workflowId),
    [workflows, workflowId],
  );

  const activeTab = useActiveTab();
  const dashboardEnabled = useWidgetsFlag();
  const automationsEnabled = useAutomationsEnabled();
  const [shareOpen, setShareOpen] = React.useState(false);
  const { order: tabOrder, reorderVisible } = useWorkflowViewTabOrder();

  // Dashboard is WIDGETS-gated; the saved order keeps its slot so a flag
  // flip restores it in place.
  const visibleTabs = React.useMemo(
    () =>
      dashboardEnabled
        ? tabOrder
        : tabOrder.filter((tab) => tab !== "dashboard"),
    [tabOrder, dashboardEnabled],
  );

  const navigateTab = React.useCallback(
    (tab: string) => {
      if (tab === activeTab) return;
      track({
        context: AnalyticsContext.Issue,
        event: IssueEvent.SwitchTab,
        data: { scope: AnalyticsScope.Workflow, tab },
      });
      replace(`/workflow/${workflowId}/${tab}`);
    },
    [activeTab, workflowId, replace, track],
  );

  // Object-level guard: the API filters the `workflows` list by visibility,
  // so a workflow missing from it after load means the viewer can't see it
  // (or it doesn't exist). Same loading rule as RequirePermission — wait for
  // `!isLoading` with a present `currentUser` so a workspace switch doesn't
  // evaluate against the previous workspace's (stale) workflow list.
  if (isLoading || !currentUser) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Spinner />
      </Box>
    );
  }

  if (!workflow) {
    return <AccessDenied />;
  }

  return (
    <Box fill="flex" direction="column">
      <Box paddingX="md" paddingTop="sm" gap="sm">
        <Box direction="row" align="center" justify="between" gap="sm">
          <PageHeaderBreadcrumb>
            <BreadcrumbItem>
              <EntityBreadcrumbLink
                onClick={() => navigateTab("board")}
                entity={{
                  kind: "workflow",
                  id: workflowId ?? "",
                  workflow: workflow ?? { name: "Workflow" },
                }}
                active
              />
            </BreadcrumbItem>
          </PageHeaderBreadcrumb>

          <Box direction="row" align="center" gap="xs">
            {/* Automations for this workflow — a popover to create, manage,
                and toggle the automations scoped here. When automations are
                active, the trigger's zap goes amber with a count. */}
            {automationsEnabled && workflowId ? (
              <WorkflowAutomationsPopover workflowId={workflowId} />
            ) : null}

            {/* Only `full_edit` viewers can manage the workflow's share list. */}
            {canManageSharing(workflow.viewerAccessLevel) ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShareOpen(true)}
                aria-label="Share workflow"
              >
                <Icon name="share-2" size="sm" color="muted" />
                <Text>Share</Text>
              </Button>
            ) : null}
          </Box>
        </Box>

        <Tabs value={activeTab} onValueChange={navigateTab}>
          <SortableTabsList order={visibleTabs} onOrderChange={reorderVisible}>
            {visibleTabs.map((tab) => (
              <SortableTab key={tab} value={tab}>
                <TabsPillIcon
                  icon={TAB_META[tab].icon}
                  color={TAB_META[tab].color}
                />
                {TAB_META[tab].label}
              </SortableTab>
            ))}
          </SortableTabsList>
        </Tabs>
      </Box>

      <Separator />
      <Outlet />

      <FilterPanel
        open={filterPanelOpen}
        onOpenChange={closeFilterPanel}
        config={issueFilterConfig}
        filters={filters}
      />

      <WorkflowShareDialog
        workflowId={shareOpen ? (workflowId ?? null) : null}
        onOpenChange={(open) => {
          if (!open) setShareOpen(false);
        }}
      />
    </Box>
  );
}
